import {
  Flex,
  Card,
  Text,
  Button,
  Link,
  InfoIcon,
  Tooltip,
  Heading,
  ExternalLinkIcon,
  Separator,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { type FragmentOf, graphql, readFragment } from '~publish/graphql'
import {
  PortalOptions,
  useGetPortalSession,
} from '../hooks/useGetPortalSession'
import { getSettingsUrl } from '../util/getSettingsUrl'
import { capitalize } from '~publish/helpers/capitalize'
import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'
import { isFreeUser } from '~publish/helpers/user'

import styles from './BillingInformation.module.css'
export const AddressAndTax_Billing = graphql(/* GraphQL */ `
  fragment AddressAndTax_Billing on Billing {
    gateway {
      gatewayId
      gatewayType
    }
    previousGateway {
      gatewayType
    }
    address {
      city
      country
      line1
      line2
      state
      postalCode
    }
    taxExempt
    taxIds {
      country
      created
      type
      value
      verification {
        status
        verifiedAddress
        verifiedName
      }
    }
  }
`)

const isValidTaxIdStatus = (status: string | null | undefined): boolean => {
  return Boolean(
    status && ['verified', 'unverified', 'pending'].includes(status),
  )
}

const DEFAULT_TAX_MESSAGING =
  'Your business status helps us determine if we need to charge you VAT (Value Added Tax) or the applicable sales tax in your region.'

type TaxId = {
  country: string | null
  created: number | null
  type: string | null
  value: string | null
  verification: {
    status: string | null
    verifiedAddress: string | null
    verifiedName: string | null
  } | null
}

const TaxIdStatus = ({ taxIds }: { taxIds: TaxId[] }): JSX.Element => {
  return (
    <Flex direction="column" gap="xs">
      {taxIds.map((taxId) => {
        const taxIdStatus = taxId.verification?.status
        const shouldRenderStatus = isValidTaxIdStatus(taxIdStatus)
        return (
          <Text key={taxId.value} color="subtle">
            Tax ID: {taxId.value}{' '}
            {shouldRenderStatus && taxIdStatus && (
              <Text color={taxIdStatus === 'verified' ? 'success' : 'warning'}>
                {capitalize(taxIdStatus)}
              </Text>
            )}
          </Text>
        )
      })}
    </Flex>
  )
}

const BusinessStatusTitle = (): JSX.Element => {
  return (
    <Text weight="medium" align="center">
      Business Status{' '}
      <Tooltip content={DEFAULT_TAX_MESSAGING}>
        <InfoIcon
          size="small"
          color="subtle"
          style={{ verticalAlign: 'text-bottom' }}
        />
      </Tooltip>
    </Text>
  )
}

const TaxStatusDetails = ({
  taxIds,
  taxExempt,
  trackPortalSession,
  portalSessionLoading,
  getCustomerPortalSession,
}: {
  taxIds?: TaxId[] | null
  taxExempt?: string | null
  trackPortalSession: ({ ctaButton }: { ctaButton: string }) => void
  portalSessionLoading: boolean
  getCustomerPortalSession: (returnURL: string) => void
}): JSX.Element => {
  const hasTaxIds = taxIds && taxIds.length > 0

  if (hasTaxIds && taxExempt !== 'exempt') {
    return (
      <Flex direction="column" gap="xs">
        <BusinessStatusTitle />
        <Flex as="span" className={styles.taxStatusContainer}>
          <Text color="subtle">Using Buffer for Business</Text>
          <span className={styles.taxStatusSeparator}>&bull;</span>
          <TaxIdStatus taxIds={taxIds} />
        </Flex>

        <Text size="sm" color="subtle">
          No further action is required if you are a business without a VAT
          number (UK and EU) or a sales tax ID (US). If you have any questions,
          please contact us at{' '}
          <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
        </Text>
      </Flex>
    )
  }

  if (taxExempt === 'exempt') {
    return (
      <Flex direction="column" gap="xs">
        <BusinessStatusTitle />
        <Text color="subtle">You are tax-exempt</Text>
        {hasTaxIds && <TaxIdStatus taxIds={taxIds} />}
        <Text size="sm" color="subtle">
          To update your tax information, please contact us at{' '}
          <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
        </Text>
      </Flex>
    )
  }

  return (
    <>
      <Flex direction="column" gap="2xs">
        <BusinessStatusTitle />
        <Text color="subtle">
          Are you a business?{' '}
          <Link
            as="button"
            onClick={(): void => {
              trackPortalSession({ ctaButton: 'addYourAddress' })
              getCustomerPortalSession(getSettingsUrl())
            }}
            disabled={portalSessionLoading}
          >
            Add your tax ID
          </Link>
        </Text>
      </Flex>
      <Text size="sm" color="subtle">
        If you are a business without a VAT number, no further action is
        required. If you have any questions please contact us at{' '}
        <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
      </Text>
    </>
  )
}

export const BillingInformation = ({
  data,
}: {
  data?: FragmentOf<typeof AddressAndTax_Billing> | null
}): JSX.Element | null => {
  const organizationId = useOrganizationId()
  const { account } = useAccount()
  const isFreePlan = isFreeUser(account)
  const { getCustomerPortalSession, loading: portalSessionLoading } =
    useGetPortalSession({
      portalOption: PortalOptions.CUSTOMER_UPDATE,
    })

  if (isFreePlan) {
    return null
  }

  const fragmentData = data ? readFragment(AddressAndTax_Billing, data) : null
  const { address, taxExempt, taxIds, gateway } = fragmentData ?? {}
  const gatewayId = gateway?.gatewayId

  const trackPortalSession = ({ ctaButton }: { ctaButton: string }): void => {
    BufferTracker.billingPortalSessionCreated({
      organizationId: organizationId ?? null,
      customerId: gatewayId,
      ctaApp: 'publish',
      ctaView: 'billing',
      ctaLocation: 'billingInformation',
      ctaButton,
      ctaVersion: '1',
    })
  }

  return (
    <>
      <Separator className={styles.fullWidth} />
      <Flex gap="xs" direction="row" align="center" justify="between" fullWidth>
        <Heading size="medium">Additional Information</Heading>
        <Button
          variant="secondary"
          onClick={(): void => {
            trackPortalSession({ ctaButton: 'updateInformation' })
            getCustomerPortalSession(getSettingsUrl())
          }}
          disabled={portalSessionLoading}
        >
          Update Information
          <ExternalLinkIcon />
        </Button>
      </Flex>
      <Card style={{ width: '100%' }}>
        <Flex direction="column" gap="md">
          {address ? (
            <Flex direction="column" gap="2xs">
              <Text weight="medium">Address</Text>
              {(address.line1 || address.line2 || address.country) && (
                <Text color="subtle">
                  {[
                    address.line1,
                    address.line2,
                    address.city,
                    address.state,
                    address.postalCode,
                    address.country,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Text>
              )}
            </Flex>
          ) : (
            <Flex direction="column" gap="2xs">
              <Text>You haven&apos;t added your address yet.</Text>
              <Link
                as="button"
                onClick={(): void => {
                  trackPortalSession({ ctaButton: 'addYourAddress' })
                  getCustomerPortalSession(getSettingsUrl())
                }}
                disabled={portalSessionLoading}
              >
                Add Your Address
              </Link>
            </Flex>
          )}
          <TaxStatusDetails
            taxIds={taxIds?.filter((tax): tax is TaxId => tax !== null)}
            taxExempt={taxExempt}
            trackPortalSession={trackPortalSession}
            portalSessionLoading={portalSessionLoading}
            getCustomerPortalSession={getCustomerPortalSession}
          />
        </Flex>
      </Card>
    </>
  )
}

export default BillingInformation
